import { useState, useEffect } from 'react';
import { settingsApi } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Save, Loader2, Send, FileText, Clock, Phone, BookOpen } from 'lucide-react';

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  const [formData, setFormData] = useState({
    teams_webhook_url: '',
    notification_email: '',
    offline_threshold_minutes: 10,
    baseline_window_minutes: 30,
    support_phone: '',
    support_email: '',
  });

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsApi.get();
      setFormData({
        teams_webhook_url: response.data.teams_webhook_url || '',
        notification_email: response.data.notification_email || '',
        offline_threshold_minutes: response.data.offline_threshold_minutes || 10,
        baseline_window_minutes: response.data.baseline_window_minutes || 30,
        support_phone: response.data.support_phone || '',
        support_email: response.data.support_email || '',
      });
    } catch {
      toast.error('Kunde inte ladda inställningar');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsApi.update(formData);
      toast.success('Inställningar sparade');
    } catch {
      toast.error('Kunde inte spara');
    } finally { setSaving(false); }
  };

  const handleSendReport = async () => {
    setSendingReport(true);
    try {
      const res = await settingsApi.sendWeeklyReport();
      const d = res.data;
      if (d.webhooks_sent > 0) {
        toast.success(`Veckorapport skickad till ${d.webhooks_sent} webhook(s) — ${d.events_this_week} händelser denna vecka`);
      } else {
        toast.info('Rapport genererad men ingen Teams-webhook konfigurerad. Lägg till en webhook-URL ovan.');
      }
    } catch {
      toast.error('Kunde inte skicka rapport');
    } finally { setSendingReport(false); }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy tracking-tight">Inställningar</h1>
        <p className="text-slate-500 mt-1 font-ibm">Konfigurera globala inställningar för din organisation</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        {/* Teams */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-chivo text-lg">Microsoft Teams</CardTitle>
            <CardDescription className="font-ibm">Konfigurera webhook för Teams-notiser och veckorapporter</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="font-ibm text-xs uppercase tracking-widest">Teams Webhook-URL</Label>
              <Input value={formData.teams_webhook_url}
                onChange={(e) => setFormData({ ...formData, teams_webhook_url: e.target.value })}
                placeholder="https://outlook.office.com/webhook/..."
                className="bg-slate-50" data-testid="teams-webhook-input" />
              <p className="text-xs text-slate-500 font-ibm">
                Skapa en Incoming Webhook i din Teams-kanal och klistra in URL:en här
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Weekly Report */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-chivo text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" /> Veckorapport
            </CardTitle>
            <CardDescription className="font-ibm">
              Automatisk veckosammanfattning skickas till Teams varje måndag kl 07:00
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Clock className="h-5 w-5 text-slate-400 shrink-0" />
              <div>
                <p className="text-sm font-ibm font-medium text-slate-700">Schema: Varje måndag 07:00</p>
                <p className="text-xs text-slate-500 font-ibm">Innehåller: antal larm, mest aktiva rum, spray-markeringar, enhetsstatus</p>
              </div>
            </div>
            <Button type="button" variant="outline" onClick={handleSendReport} disabled={sendingReport}
              className="font-ibm gap-2 w-full" data-testid="send-weekly-report-btn">
              {sendingReport ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Skicka veckorapport nu (test)
            </Button>
          </CardContent>
        </Card>

        {/* Email */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-chivo text-lg">E-post</CardTitle>
            <CardDescription className="font-ibm">Standard e-post för organisationsbreda varningar</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="font-ibm text-xs uppercase tracking-widest">Notifikations-e-post</Label>
              <Input type="email" value={formData.notification_email}
                onChange={(e) => setFormData({ ...formData, notification_email: e.target.value })}
                placeholder="larm@dinskola.se" className="bg-slate-50" data-testid="notification-email-input" />
            </div>
          </CardContent>
        </Card>

        {/* Support Contact (Onboarding Guide) */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-chivo text-lg flex items-center gap-2">
              <BookOpen className="h-5 w-5" /> Supportkontakt (Onboarding-guide)
            </CardTitle>
            <CardDescription className="font-ibm">Kontaktuppgifter som visas i den utskrivbara onboarding-guiden för skolpersonal</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Telefon</Label>
                <Input value={formData.support_phone}
                  onChange={(e) => setFormData({ ...formData, support_phone: e.target.value })}
                  placeholder="070-123 45 67"
                  className="bg-slate-50" data-testid="support-phone-input" />
              </div>
              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Support-e-post</Label>
                <Input type="email" value={formData.support_email}
                  onChange={(e) => setFormData({ ...formData, support_email: e.target.value })}
                  placeholder="support@ertforetag.se"
                  className="bg-slate-50" data-testid="support-email-input" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detection */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-chivo text-lg">Detektionsinställningar</CardTitle>
            <CardDescription className="font-ibm">Konfigurera detektionsmotorns parametrar</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Offline-tröskel</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min="1" max="60" value={formData.offline_threshold_minutes}
                    onChange={(e) => setFormData({ ...formData, offline_threshold_minutes: parseInt(e.target.value) || 10 })}
                    className="bg-slate-50" data-testid="offline-threshold-input" />
                  <span className="text-sm text-slate-500 font-ibm">min</span>
                </div>
                <p className="text-xs text-slate-500 font-ibm">Markera enhet som offline efter denna tid utan data</p>
              </div>
              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Baslinje-fönster</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min="10" max="120" value={formData.baseline_window_minutes}
                    onChange={(e) => setFormData({ ...formData, baseline_window_minutes: parseInt(e.target.value) || 30 })}
                    className="bg-slate-50" data-testid="baseline-window-input" />
                  <span className="text-sm text-slate-500 font-ibm">min</span>
                </div>
                <p className="text-xs text-slate-500 font-ibm">Rullande fönster för baslinjeberäkning</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button type="submit" className="bg-navy hover:bg-navy/90 rounded-sm font-ibm gap-2" disabled={saving} data-testid="save-settings-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Spara inställningar
        </Button>
      </form>

      {/* Demo tools */}
      <Card className="border-amber-200 bg-amber-50/30 dark:bg-amber-950/10">
        <CardHeader>
          <CardTitle className="font-chivo text-base text-amber-800">Demo-verktyg</CardTitle>
          <CardDescription className="font-ibm text-xs">Verktyg for demonstrationer och presentationer</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button variant="outline" className="gap-2" data-testid="seed-demo-btn" onClick={async () => {
            toast.info('Skapar demo-data...');
            try {
              const API = process.env.REACT_APP_BACKEND_URL;
              const res = await fetch(`${API}/api/admin/seed-demo-data`, {
                method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
              });
              const d = await res.json();
              if (d.ok) toast.success(d.message);
              else toast.error(d.message || 'Kunde inte skapa demo-data');
            } catch { toast.error('Fel vid demo-data'); }
          }}>
            <FileText className="h-4 w-4" /> Skapa demo-data
          </Button>
          <Button variant="outline" className="gap-2" data-testid="download-pres-btn" onClick={async () => {
            try {
              const API = process.env.REACT_APP_BACKEND_URL;
              const res = await fetch(`${API}/api/sales/presentation-pdf`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
              if (!res.ok) throw new Error();
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = 'Vapelarm_Presentation.pdf';
              document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
              toast.success('Presentation nedladdad');
            } catch { toast.error('Kunde inte ladda ner'); }
          }}>
            <BookOpen className="h-4 w-4" /> Ladda ner presentation (PDF)
          </Button>
          <Button variant="outline" className="gap-2" onClick={async () => {
            try {
              const API = process.env.REACT_APP_BACKEND_URL;
              const res = await fetch(`${API}/api/sales/beslutsunderlag?variant=general`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
              if (!res.ok) throw new Error();
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = 'Vapelarm_Beslutsunderlag.pdf';
              document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
              toast.success('Beslutsunderlag nedladdat');
            } catch { toast.error('Kunde inte ladda ner'); }
          }}>
            <BookOpen className="h-4 w-4" /> Beslutsunderlag 5.0 (Generell)
          </Button>
          <Button variant="outline" className="gap-2" onClick={async () => {
            try {
              const API = process.env.REACT_APP_BACKEND_URL;
              const res = await fetch(`${API}/api/sales/beslutsunderlag?variant=falkenberg`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
              if (!res.ok) throw new Error();
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = 'Vapelarm_Beslutsunderlag_Falkenberg.pdf';
              document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
              toast.success('Falkenberg-version nedladdad');
            } catch { toast.error('Kunde inte ladda ner'); }
          }}>
            <BookOpen className="h-4 w-4" /> Beslutsunderlag v3 (Falkenberg)
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
